import { PanelLeftClose } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUiStore } from '@/app/store/ui.store';
import type { NavGroup, NavItem } from '@/shared/types/navigation';

function isItemActive(item: NavItem, pathname: string) {
  if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) return true;
  return item.children?.some((child) => pathname === child.path || pathname.startsWith(`${child.path}/`)) ?? false;
}

interface SidebarProps {
  navGroups: NavGroup[];
}

export function Sidebar({ navGroups }: SidebarProps) {
  const { pathname } = useLocation();
  const isCollapsed = useUiStore((s) => s.isSidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  // SHCT-821 — the native `title` tooltip is for the COLLAPSED rail, where only the icon shows and the
  // label is otherwise unreadable. While expanded the label is already on screen, so the tooltip added
  // nothing and actively misled: the browser draws it below the cursor, which lands it beside the NEXT
  // menu row, so hovering 관리지침 painted the word "관리지침" next to 질병코드 and read as a menu whose
  // name disagreed with itself. The text is always the item's own LNB label — only when it is shown
  // changes here.
  const tip = (label: string) => (isCollapsed ? label : undefined);

  return (
    <aside className={`lnb ${isCollapsed ? 'lnb--collapsed' : ''}`}>
      <nav className="lnb__nav" aria-label="Sidebar navigation">
        {navGroups.map((group) => (
          <div className="lnb__group" key={group.label}>
            {group.label && <div className="lnb__group-label">{group.label}</div>}

            {group.items.map((item) => {
              const Icon = item.icon;
              const active = isItemActive(item, pathname);

              if (!item.children?.length && item.path) {
                return (
                  <NavLink
                    key={`${group.label}-${item.label}`}
                    to={item.path}
                    className={({ isActive }) => `lnb__item ${isActive ? 'lnb__item--active' : ''}`}
                    title={tip(item.label)}
                  >
                    <Icon />
                    <span className="lnb__item-text">{item.label}</span>
                  </NavLink>
                );
              }

              return (
                <div key={`${group.label}-${item.label}`}>
                  <button
                    type="button"
                    className={`lnb__item ${active ? 'lnb__item--active' : ''}`}
                    data-expanded="true"
                    title={tip(item.label)}
                  >
                    <Icon />
                    <span className="lnb__item-text">{item.label}</span>
                  </button>

                  <div className="lnb__submenu" data-open="true">
                    {item.children?.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) => `lnb__submenu-item ${isActive ? 'lnb__submenu-item--active' : ''}`}
                        title={tip(child.label)}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="lnb__footer">
        <button type="button" className="lnb__collapse-btn" onClick={toggleSidebar}>
          <PanelLeftClose />
          <span>Collapse sidebar</span>
        </button>
      </div>
    </aside>
  );
}
